/*
 * Warehouse Explorer — read-only ad-hoc query against the warehouse
 * (ClickHouse when configured, in-memory buckets otherwise). Three
 * lenses: total, grouped, time series. Filters are intentionally
 * minimal — this surface is for "show me totals quickly", not the
 * full Analytics Explorer's pivot/chart UI.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonStat, SkeletonTable } from "../components/Skeleton.js";

type GroupBy = "metric" | "program" | "period" | "orgUnit";

export default function WarehouseExplorer() {
  const [groupBy, setGroupBy] = useState<GroupBy>("metric");
  const [metricFilter, setMetricFilter] = useState("");
  const [total, setTotal] = useState<number | null>(null);
  const [rows, setRows] = useState<Array<{ key: string; total: number }> | null>(null);
  const [series, setSeries] = useState<Array<{ periodId: string; total: number }> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const metricIds = useMemo(() => metricFilter.split(",").map((s) => s.trim()).filter(Boolean), [metricFilter]);

  const refresh = async () => {
    setError(null);
    try {
      const params = metricIds.length ? { metricIds } : undefined;
      const [s, g, t] = await Promise.all([
        api.warehouseSummary(params),
        api.warehouseGrouped({ ...(params ?? {}), groupBy }),
        api.warehouseTimeseries(params)
      ]);
      setTotal(s.total);
      setRows(g.rows);
      setSeries(t.rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };
  useEffect(() => { void refresh(); /* eslint-disable-next-line */ }, [groupBy, metricFilter]);

  const seriesMax = useMemo(() => {
    if (!series || series.length === 0) return 0;
    return Math.max(...series.map((p) => p.total));
  }, [series]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Warehouse explorer</h1>
          <p className="muted">
            Quick totals + groupings against the warehouse. For deep pivot/chart analytics, use Analytics explorer.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={() => void refresh()}>Refresh</button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters">
        <label className="transfer-filter transfer-filter-grow">
          Metric IDs (comma-separated, blank = all)
          <input type="search" placeholder="tally:event-count:prog-1, tally:value-sum:de-doses…" value={metricFilter} onChange={(e) => setMetricFilter(e.target.value)} />
        </label>
        <label className="transfer-filter">
          Group by
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value as GroupBy)}>
            <option value="metric">Metric</option>
            <option value="program">Program</option>
            <option value="period">Period</option>
            <option value="orgUnit">Org unit</option>
          </select>
        </label>
      </section>

      <section className="stat-grid">
        {total === null ? <SkeletonStat /> : (
          <div className="stat">
            <div className="stat-label">Total (filtered)</div>
            <div className="stat-value">{Math.round(total).toLocaleString()}</div>
            <div className="stat-hint">{rows?.length ?? 0} groups · {series?.length ?? 0} periods</div>
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-head"><h2>Grouped totals — by {groupBy}</h2></div>
        {!rows && <SkeletonTable rows={6} columns={2} />}
        {rows && rows.length === 0 && (
          <div className="empty-state-card">
            <h2 style={{ marginTop: 0 }}>No buckets match this filter</h2>
            <p>Adjust the metric filter, or sync more data first. TallyMetrics generates buckets as tracker events land.</p>
          </div>
        )}
        {rows && rows.length > 0 && (
          <table className="transfer-table">
            <thead><tr><th>{groupBy}</th><th style={{ textAlign: "right" }}>Total</th></tr></thead>
            <tbody>
              {rows.sort((a, b) => b.total - a.total).slice(0, 50).map((r) => (
                <tr key={r.key}>
                  <td className="mono" style={{ fontSize: 12 }}>{r.key}</td>
                  <td style={{ textAlign: "right" }}>{Math.round(r.total).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {series && series.length > 0 && (
        <section className="section">
          <div className="section-head"><h2>Time series — by period</h2></div>
          <div className="warehouse-series">
            {series.map((p) => (
              <div key={p.periodId} className="warehouse-series-bar" title={`${p.periodId}: ${p.total}`}>
                <div className="warehouse-series-fill" style={{ height: `${seriesMax > 0 ? (p.total / seriesMax) * 100 : 0}%` }} />
                <div className="warehouse-series-label">{p.periodId.slice(-2)}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
